import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";

/**
 * OTP request / resend / registration. The session itself (token, user record)
 * lives in user.slice — this slice only owns the pre-login handshake, mirroring
 * the web app's auth.reducer / user.reducer split.
 */

/**
 * `portal: 'supplier'` restricts the login to supplier accounts: the server
 * refuses a rider/driver's credentials and will not auto-create an account for
 * an unknown number. Set on the thunk rather than at the call site so no caller
 * can omit it and silently widen who may sign in here.
 */
export const loginSubmit = apiThunk<any, any>("auth/loginSubmit", {
  server: "login",
  url: "cust/login-otp",
  method: "POST",
  body: (data) => ({ ...data, portal: "supplier" }),
  successMsg: true,
  errorMsg: true,
});

export const resendOTP = apiThunk<any, any>("auth/resendOTP", {
  server: "login",
  url: "cust/resend-otp",
  method: "POST",
  successMsg: true,
  errorMsg: true,
});

export const loginAttempts = apiThunk<any, void>("auth/loginAttempts", {
  server: "login",
  url: "cust/login-attempts",
  method: "GET",
  errorMsg: true,
});

/**
 * Registration, step 1 — basic details. Sends an OTP to the given mobile and
 * returns the pending account id, which step 2 verifies against.
 *
 * Requires a fresh reCAPTCHA 'REGISTER' token: the server calls verifyCaptcha
 * directly and rejects the call without one.
 */
export const registerSubmit = apiThunk<any, any>("auth/registerSubmit", {
  server: "login",
  url: "cust/register",
  method: "POST",
  errorMsg: true,
});

/**
 * Registration, step 2 — verify the OTP. On success the server marks the
 * account verified and returns a session, so the user is logged in and step 3
 * can call the authenticated register-profile endpoint.
 */
export const registerVerifyOTP = apiThunk<any, { id: string; otp: string }>(
  "auth/registerVerifyOTP",
  {
    server: "login",
    url: "cust/register-otp",
    method: "POST",
    errorMsg: true,
  }
);


/**
 * Registration, step 3 — the company details, saved once the account is
 * verified. Authenticated: the server reads the customer from the JWT, so a
 * caller cannot file an application against someone else's account.
 *
 * Creates the a_suppliers row as status='pending'. The applicant cannot sign in
 * until an admin approves it.
 */
export const registerProfile = apiThunk<any, any>("auth/registerProfile", {
  server: "login",
  url: "suppliers/register",
  method: "POST",
  errorMsg: true,
});

/** Which pane of the registration form is showing. */
export type RegisterStep = "details" | "otp" | "profile";

interface AuthState {
  loginDetails: any;
  registerDetails: any;
  loginVerifyStep: boolean | null;
  registerVerifyStep: boolean | null;
  attempts: any;
  globalData: any;
  errors: any;
  loading: boolean;
  /** ISO-8601 expiry from the server. Null when no OTP is in flight. */
  otpExpiresAt: string | null;

  // --- Registration ---
  registerStep: RegisterStep;
  /** The pending (unverified) account id returned by step 1. */
  registerId: string | null;
  /** Step 1's values, kept so the OTP pane can show the number and resend. */
  registerBasics: any;
  /** Set once step 3 saves, so the screen can show its done state. */
  registerCompleted: boolean;
  registerLoading: boolean;
  registerError: string | null;
}

const initialState: AuthState = {
  loginDetails: null,
  registerDetails: null,
  loginVerifyStep: null,
  registerVerifyStep: null,
  attempts: null,
  globalData: null,
  errors: null,
  loading: false,
  otpExpiresAt: null,

  registerStep: "details",
  registerId: null,
  registerBasics: null,
  registerCompleted: false,
  registerLoading: false,
  registerError: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginDetailsSave: (state, action: PayloadAction<any>) => {
      state.loginDetails = action.payload;
    },
    setGlobalData: (state, action: PayloadAction<any>) => {
      state.globalData = action.payload;
    },
    resetLoginVerifyStep: (state) => {
      state.loginVerifyStep = null;
    },
    resetRegisterVerifyStep: (state) => {
      state.registerVerifyStep = null;
    },
    /** Abandon a half-finished registration and return to step 1. */
    resetRegistration: (state) => {
      state.registerStep = "details";
      state.registerId = null;
      state.registerBasics = null;
      state.registerCompleted = false;
      state.registerError = null;
      state.otpExpiresAt = null;
    },
    clearRegisterError: (state) => {
      state.registerError = null;
    },
    clearLoginData: (state) => {
      state.loginDetails = null;
      state.registerDetails = null;
      state.loginVerifyStep = null;
      state.registerVerifyStep = null;
      state.otpExpiresAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginSubmit.pending, (state) => {
        state.errors = null;
        state.loginVerifyStep = null;
        state.loading = true;
      })
      .addCase(loginSubmit.fulfilled, (state, action) => {
        state.loginVerifyStep = true;
        state.loading = false;
        // Server-supplied expiry drives the live countdown on the verify screen.
        state.otpExpiresAt = action.payload?.otp_expires_at ?? null;
      })
      .addCase(loginSubmit.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(resendOTP.fulfilled, (state, action) => {
        state.otpExpiresAt = action.payload?.otp_expires_at ?? null;
        state.loading = false;
      })
      .addCase(resendOTP.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendOTP.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(loginAttempts.pending, (state) => {
        state.errors = null;
        state.loading = true;
      })
      .addCase(loginAttempts.fulfilled, (state, action) => {
        state.attempts = action.payload;
        state.loading = false;
      })
      .addCase(loginAttempts.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      // --- Registration step 1: basic details → OTP sent ---
      .addCase(registerSubmit.pending, (state) => {
        state.registerError = null;
        state.registerLoading = true;
      })
      .addCase(registerSubmit.fulfilled, (state, action) => {
        state.registerLoading = false;
        const response = action.payload;

        // The route replies 201 with the pending account id. Advance only when
        // that id is actually present, otherwise the OTP pane has nothing to
        // verify against.
        if (response?.id) {
          state.registerDetails = response;
          state.registerId = String(response.id);
          state.registerBasics = action.meta.arg;
          state.registerStep = "otp";
          state.otpExpiresAt = response.otp_expires_at ?? null;
        } else {
          state.registerError = response?.msg ?? "Could not start registration.";
        }
      })
      .addCase(registerSubmit.rejected, (state, action) => {
        state.registerLoading = false;
        state.registerError = action.payload?.msg ?? "Could not start registration.";
      })

      // --- Registration step 2: OTP verified → session established ---
      .addCase(registerVerifyOTP.pending, (state) => {
        state.registerError = null;
        state.registerLoading = true;
      })
      .addCase(registerVerifyOTP.fulfilled, (state, action) => {
        state.registerLoading = false;
        if (action.payload?.status === "success") {
          state.registerStep = "profile";
        } else {
          state.registerError = action.payload?.msg ?? "Incorrect or expired code.";
        }
      })
      .addCase(registerVerifyOTP.rejected, (state, action) => {
        state.registerLoading = false;
        state.registerError = action.payload?.msg ?? "Incorrect or expired code.";
      })

      // --- Registration step 3: remaining details saved ---
      .addCase(registerProfile.pending, (state) => {
        state.registerError = null;
        state.registerLoading = true;
      })
      .addCase(registerProfile.fulfilled, (state, action) => {
        state.registerLoading = false;
        // suppliers/register returns a proper 4xx on failure (handled by the
        // rejected case). This guards the older shape where a route replies 200
        // with {status:"error"} in the body.
        if (action.payload?.status === "error") {
          state.registerError = action.payload?.msg || action.payload?.errors || "Could not save details.";
        } else {
          state.registerCompleted = true;
        }
      })
      .addCase(registerProfile.rejected, (state, action) => {
        state.registerLoading = false;
        state.registerError = action.payload?.msg ?? "Could not save your details.";
      })

      // Logout (the user/logout reducer or the logoutRequest thunk) must drop
      // the finished OTP handshake too — a lingering loginVerifyStep makes the
      // Login screen bounce straight to /login-verify. Matched by type because
      // user.slice already imports this file.
      .addMatcher(
        (action) =>
          // pending too: the header navigates to "/" before the call returns.
          ["user/logout", "user/logout/pending", "user/logout/fulfilled", "user/logout/rejected"].includes(
            action.type
          ),
        () => initialState
      );
  },
});

export const {
  loginDetailsSave,
  setGlobalData,
  resetLoginVerifyStep,
  resetRegisterVerifyStep,
  resetRegistration,
  clearRegisterError,
  clearLoginData,
} = authSlice.actions;

export default authSlice.reducer;
